import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Popover, Spin } from 'antd';
import { DoubleLeftOutlined, DoubleRightOutlined } from '@ant-design/icons';
import { FiChevronDown } from 'react-icons/fi';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import { getAttendanceRequests } from 'actions/profileNew';
import AttendanceRequestTable from 'pages/userManagementV3/localComponent/attendanceRequestTable';
import AttendanceRequestsModal from 'pages/profile/attendanceRequestsModal';
import 'pages/userManagementV3/profileLeavesStyles.css';
import 'pages/userManagementV3/localComponent/tabsStyles.css';

// "Attendance Requests" block for the Users V2 profile Attendance tab — same
// year / month pills as the Leaves tab "Leave Requests" header
// (userManagementV3/profileLeaves.js) and the V3 attendance request table,
// without the MARK ATTENDANCE button.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const AttendanceRequestsSection = ({ userId, attendanceRequests, getAttendanceRequests }) => {
    const currentYear = moment().year();
    const currentMonthIndex = moment().month();
    const [isMonth, setIsMonth] = useState(moment().format('MMM'));
    const [isYear, setIsYear] = useState(moment().format('YYYY'));
    const [monthOpen, setMonthOpen] = useState(false);
    const [yearOpen, setYearOpen] = useState(false);
    const [decadeStart, setDecadeStart] = useState(() => Math.floor(currentYear / 10) * 10);
    const [loading, setLoading] = useState(false);
    const [attRequestModal, setAttRequestModal] = useState(false);
    const [storeRowData, setStoreRowData] = useState(null);

    const fetchRequests = useCallback(() => {
        if (!userId) return;
        setLoading(true);
        getAttendanceRequests({ userId, year: isYear, month: isMonth, page: 1, limit: 100 }, () => setLoading(false));
    }, [userId, isYear, isMonth, getAttendanceRequests]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    // Keep the popovers attached to their pills while the profile scrolls.
    useEffect(() => {
        if (!yearOpen && !monthOpen) return undefined;
        const handleScroll = () => window.dispatchEvent(new Event('resize'));
        const scrollContainers = document.querySelectorAll('.staff-profile-info-scroll, .dv2-outer-scroll, #contentWrapper');
        scrollContainers.forEach((c) => c.addEventListener('scroll', handleScroll, { passive: true }));
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            scrollContainers.forEach((c) => c.removeEventListener('scroll', handleScroll));
            window.removeEventListener('scroll', handleScroll);
        };
    }, [yearOpen, monthOpen]);

    const yearItems = [];
    for (let i = -1; i <= 10; i++) yearItems.push(decadeStart + i);
    const isNextDecadeDisabled = (decadeStart + 10) > currentYear;

    const handleYearSelect = (y) => {
        if (y > currentYear) return;
        setIsYear(String(y));
        setYearOpen(false);
        if (y === currentYear && moment(isMonth, 'MMM').month() > currentMonthIndex) {
            setIsMonth(moment().format('MMM'));
        }
    };

    const isMonthDisabled = (monthIndex) => {
        const selectedYear = Number(isYear);
        if (selectedYear > currentYear) return true;
        if (selectedYear === currentYear) return monthIndex > currentMonthIndex;
        return false;
    };

    const handleMonthSelect = (m, idx) => {
        if (isMonthDisabled(idx)) return;
        setIsMonth(m);
        setMonthOpen(false);
    };

    const monthPopoverContent = (
        <div className="leave-month-popover-card">
            <div className="leave-picker-body">
                <div className="leave-picker-grid">
                    {MONTHS.map((m, idx) => {
                        const isSelected = isMonth === m;
                        const isDisabled = isMonthDisabled(idx);
                        return (
                            <div
                                key={m}
                                className={`leave-picker-cell ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => { if (!isDisabled) handleMonthSelect(m, idx); }}
                            >
                                {m}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const yearPopoverContent = (
        <div className="leave-year-popover-card">
            <div className="leave-picker-header">
                <DoubleLeftOutlined
                    className="leave-picker-nav-btn"
                    onClick={(e) => { e.stopPropagation(); setDecadeStart((prev) => prev - 10); }}
                />
                <span className="leave-picker-header-title">{decadeStart}-{decadeStart + 9}</span>
                <DoubleRightOutlined
                    className={`leave-picker-nav-btn ${isNextDecadeDisabled ? 'disabled' : ''}`}
                    onClick={(e) => { e.stopPropagation(); if (!isNextDecadeDisabled) setDecadeStart((prev) => prev + 10); }}
                />
            </div>
            <div className="leave-picker-body">
                <div className="leave-picker-grid">
                    {yearItems.map((y) => {
                        const isOutOfDecade = y < decadeStart || y > decadeStart + 9;
                        const isFutureYear = y > currentYear;
                        const isSelected = String(y) === String(isYear);
                        return (
                            <div
                                key={y}
                                className={`leave-picker-cell ${isOutOfDecade ? 'other-decade' : ''} ${isSelected ? 'selected' : ''} ${isFutureYear ? 'disabled' : ''}`}
                                onClick={() => { if (!isFutureYear) handleYearSelect(y); }}
                            >
                                {y}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ backgroundColor: "#FFFFFF", borderRadius: fluid(24), padding: fluid(24), boxSizing: "border-box", width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: fluid(12), marginBottom: fluid(20), flexWrap: "wrap" }}>
                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(20), fontWeight: 500, color: "#191919" }}>
                    Attendance Requests
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Popover
                        content={yearPopoverContent}
                        trigger="click"
                        open={yearOpen}
                        onOpenChange={setYearOpen}
                        placement="topLeft"
                        overlayClassName="leave-picker-popover"
                    >
                        <div className={`leave-filter-pill ${yearOpen ? 'active' : ''}`}>
                            <span>{isYear}</span>
                            <FiChevronDown className="custom-icon" />
                        </div>
                    </Popover>
                    <Popover
                        content={monthPopoverContent}
                        trigger="click"
                        open={monthOpen}
                        onOpenChange={setMonthOpen}
                        placement="topLeft"
                        overlayClassName="leave-picker-popover"
                    >
                        <div className={`leave-filter-pill ${monthOpen ? 'active' : ''}`}>
                            <span>{isMonth}</span>
                            <FiChevronDown className="custom-icon" />
                        </div>
                    </Popover>
                </div>
            </div>

            <Spin spinning={loading}>
                <AttendanceRequestTable
                    dataSource={attendanceRequests?.items || []}
                    setAttRequestModal={setAttRequestModal}
                    setStoreRowData={setStoreRowData}
                    storeRowData={storeRowData}
                    successCallback={fetchRequests}
                    hidePagination
                />
            </Spin>

            {attRequestModal && (
                <AttendanceRequestsModal
                    open={attRequestModal}
                    onClose={() => setAttRequestModal(false)}
                    data={storeRowData}
                    successCallback={fetchRequests}
                    setStoreRowData={setStoreRowData}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    attendanceRequests: state.profileNew.attendanceRequests,
});

const mapDispatchToProps = (dispatch) => ({
    getAttendanceRequests: (params, callback) => dispatch(getAttendanceRequests(params, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AttendanceRequestsSection);
